import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Button, InformationalScreen, LoadingModal, Stack, Text, } from '@adc/parallax-component-library';
import { ApiStatus, ErrorCode } from 'Constants';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useApiError, useAuth } from 'Hooks';
import { Footer, FormContainer, Input, MainContainer, TopHeaderBackArrow, } from 'Components/utility';
import i18n from 'Utilities/i18n';
import * as log from 'Utilities/log';
import mediator from 'Utilities/mediator';
import { forgotPass } from 'Services/auth';
const initialValuesFormik = { email: '' };
const mapStateToProps = ({ env: { osType, osVersion, app, appVersion } }) => {
    return {
        osType,
        osVersion,
        app,
        appVersion,
    };
};
const connector = connect(mapStateToProps);
const ForgotPasswordPage = ({ osType, osVersion, app, appVersion }) => {
    const [pageIndex, setPageIndex] = useState(0);
    const { showApiErrorModal, showNetworkErrorModal } = useApiError();
    const { showHcpError, showForgotPasswordBlockedMessage, showForgotPasswordConfirmationMessage } = useAuth();
    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .email(i18n.t('ForgotPassword.content.forgotPasswordForm.formField.email.errors.emailAddressValidEmail'))
            .required(i18n.t('ForgotPassword.content.forgotPasswordForm.formField.email.errors.required')),
    });
    const handleApiRequestError = useCallback((error) => {
        switch (error.status) {
            case ApiStatus.FORBIDDEN: {
                const code = error.response?.data.code;
                if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
                    showHcpError();
                }
                break;
            }
            case ApiStatus.TOO_MANY_REQUESTS:
                showForgotPasswordBlockedMessage();
                break;
            default:
                error.code === ErrorCode.NETWORK_ERROR ? showNetworkErrorModal() : showApiErrorModal();
        }
    }, [showApiErrorModal, showForgotPasswordBlockedMessage, showHcpError, showNetworkErrorModal]);
    const handleSubmitForgotPass = useCallback((email) => {
        return new Promise((resolve, reject) => {
            forgotPass(email, osType, osVersion, app, appVersion)
                .then(resolve)
                .catch((error) => {
                handleApiRequestError(error);
                reject(error);
            });
        });
    }, [osType, osVersion, app, appVersion, handleApiRequestError]);
    const onSubmitForm = useCallback(async (values, { setSubmitting }) => {
        setSubmitting(true);
        try {
            await handleSubmitForgotPass(values.email);
            setPageIndex(1);
        }
        catch (err) {
            log.error(err);
        }
        finally {
            setSubmitting(false);
        }
    }, [handleSubmitForgotPass]);
    const { handleSubmit, handleChange, handleBlur, isValid, isSubmitting, errors, touched, values } = useFormik({
        initialValues: initialValuesFormik,
        validationSchema,
        onSubmit: onSubmitForm,
        validateOnBlur: true,
    });
    const handleResendEmail = () => {
        handleSubmitForgotPass(values.email)
            .then(showForgotPasswordConfirmationMessage)
            .catch((err) => {
            log.error(err);
        });
    };
    const handleClickDone = () => {
        mediator.publish('router:navigate', '/login');
    };
    const handleClickBack = () => {
        if (pageIndex === 1) {
            setPageIndex(0);
            return;
        }
        mediator.publish('router:back');
    };
    return (React.createElement(MainContainer, null,
        isSubmitting && React.createElement(LoadingModal, { "aria-label": "Loading content" }),
        React.createElement(InformationalScreen, { index: pageIndex, testID: "ForgotPassword" },
            React.createElement(InformationalScreen.Page, { topNavigationProps: {
                    title: i18n.t('ForgotPassword.modals.322.title'),
                    buttonStart: {
                        element: React.createElement(TopHeaderBackArrow, { testID: "ForgotPass.step1-go-back" }),
                        label: 'ForgotPass.step1-go-back',
                        onPress: handleClickBack,
                    },
                } },
                React.createElement(InformationalScreen.Content, null,
                    React.createElement(Text, { testID: "ForgotPassword.subtitle", nativeID: "ForgotPassword.subtitle" }, i18n.t('ForgotPassword.subtitle')),
                    React.createElement(FormContainer, null,
                        React.createElement(Input, { "aria-label": i18n.t('ForgotPassword.content.forgotPasswordForm.formField.email.label'), placeholder: i18n.t('ForgotPassword.content.forgotPasswordForm.formField.email.placeholder'), label: i18n.t('ForgotPassword.content.forgotPasswordForm.formField.email.label'), errorMessage: errors.email, isInvalid: errors.email && touched.email ? true : false, testID: "email", value: values.email, marginTop: "$4", onChangeText: handleChange('email'), onBlur: handleBlur('email'), inputMode: "email" }))),
                React.createElement(InformationalScreen.Footer, null,
                    React.createElement(Footer, { buttonLabel: i18n.t('ForgotPassword.content.forgotPasswordForm.links.sendToEmail.label'), onButtonPress: handleSubmit, buttonTestID: "ForgotPass.sendLinkToEmail", disabled: !values.email || !isValid || isSubmitting }))),
            React.createElement(InformationalScreen.Page, { topNavigationProps: {
                    title: i18n.t('CheckYourEmail.modals.322.title'),
                    buttonStart: {
                        element: React.createElement(TopHeaderBackArrow, { testID: "ForgotPass.step2-go-back" }),
                        label: 'ForgotPass.step2-go-back',
                        onPress: handleClickBack,
                    },
                } },
                React.createElement(InformationalScreen.Content, null,
                    React.createElement(Text, { testID: "ForgotPass.instructionsResetSendTo" }, i18n.t('CheckYourEmail.subtitle')),
                    React.createElement(Text, { style: {
                            wordBreak: 'break-word',
                        }, type: "$bodyBase.strong", testID: "ForgotPassword.user-email" }, values.email),
                    React.createElement("img", { src: i18n.t('CheckYourEmail.content.checkYourEmail.media.checkYourEmail'), alt: "", style: {
                            objectFit: 'contain',
                            height: 190,
                        }, id: "ForgotPassword.check-email-img" }),
                    React.createElement(Stack, { gap: 6 },
                        React.createElement(Text, { marginTop: "$4", textAlign: "center", testID: "ForgotPassword.notReceiveEmail" }, i18n.t('CheckYourEmail.content.checkYourEmail.primaryText')),
                        React.createElement(Stack, { flexDirection: "row", justifyContent: "center" },
                            React.createElement(Button, { variant: "tertiary", label: i18n.t('CheckYourEmail.content.checkYourEmail.links.resendEmail.label'), "aria-label": i18n.t('CheckYourEmail.content.checkYourEmail.links.resendEmail.label'), onPress: handleResendEmail, testID: "ForgotPassword.resendEmail-btn" })))),
                React.createElement(InformationalScreen.Footer, null,
                    React.createElement(Footer, { buttonLabel: i18n.t('CheckYourEmail.content.checkYourEmail.links.done.label'), onButtonPress: handleClickDone, buttonTestID: "ForgotPassword.done-btn" }))))));
};
export default connector(ForgotPasswordPage);
